import { promiseTimer } from 'utils/debugUtils';
import { downloadFile, downloadFileGet, get, post, put } from '../apiRequest';

export type SettlementId = number;

export type SettlementStatus =
  | 'SettlementPending'
  | 'AwaitingClientApproval'
  | 'AwaitingCounterpartyReview'
  | 'ViewedByCounterparty'
  | 'SignedByCounterparty'
  | 'DeclinedByCounterparty'
  | 'SettlementExecuted'
  | 'SettlementResolved'
  | 'SettlementPaid';

export interface ClaimValue {
  claimId: number;
  name: string;
  proposedSecured: number;
  proposedAdministrative: number;
  proposedPriority: number;
  proposedUnsecured: number;
  proposedTotal: number;
  currentSecured: number;
  currentAdministrative: number;
  currentPriority: number;
  currentUnsecured: number;
  currentTotal: number;
}

export interface SingleSettlementResponse {
  id: SettlementId;
  settlementNumber: number;
  statusId: number;
  status: SettlementStatus;
  statusChanged: string;
  counterpartyName: string;
  counterpartyEmail: string | null;
  counterpartyPhone: string | null;
  counterpartyCountry: string | null;
  counterpartyState: string | null;
  counterpartyCity: string | null;
  counterpartyZip: string | null;
  counterpartyAddress: string | null;
  notes: string | null;
  additionalEmailRecipients: string | null;
  claims: ClaimValue[];
  claimsTotal: ClaimValue;
  agreementFileName: string | null;
}

export interface SettlementDocumentGet {
  id: number;
  settlementId: number;
  documentName: string;
  displayName: string;
  settlementDocumentTypeId: number;
  docketNumber: string;
  createdBy: string;
  createdDate: Date;
}

export type SettlementDocumentPost = Pick<
  SettlementDocumentGet,
  'settlementDocumentTypeId' | 'documentName' | 'displayName' | 'docketNumber'
>;

export interface SettlementStatusResponse {
  id: number;
  status: string;
  description: string;
}

export type SettlementDataPut = Pick<
  SingleSettlementResponse,
  | 'id'
  | 'counterpartyName'
  | 'counterpartyEmail'
  | 'counterpartyPhone'
  | 'counterpartyCountry'
  | 'counterpartyState'
  | 'counterpartyCity'
  | 'counterpartyZip'
  | 'counterpartyAddress'
  | 'additionalEmailRecipients'
  | 'notes'
> & {
  emailSend: boolean;
  emailCustomText: string;
  settlementUsersAssignment: number[];
  settlementAdminsAssignment: number[];
};

export type SettlementGenerateAgreementResponse = Pick<
  SingleSettlementResponse,
  'agreementFileName'
>;

export interface SettlementDocumentType {
  id: number;
  type: string;
  description: string;
}

export interface SettlementEmailPreview {
  text: string;
}

const EMAIL_PREVIEW_TEXT: SettlementEmailPreview = {
  text: `
  <html>
    <head>
	<style type="text/css">
		.oneLine
		{
			margin-bottom:15.0pt;
			line-height:13.65pt;
			vertical-align:baseline;
		}
      	</style>
    </head>
    <body>
        <p>&nbsp;</p>
        <p class="oneLine"><strong>Date: &nbsp;{{SettlementDate}}</strong></p>
        <p><strong>To:</strong> &nbsp;{{ClaimantName}}</p>
        <p class="oneLine"><strong>Subject:</strong> &nbsp;Confidential Settlement Offer | In re PG&amp;E Corporation, et al., Case No. 19-30088 (DM) (Bankr. N.D. Cal.)</p>
        <p><br></p>
        <p class='oneLine'>THIS CONFIDENTIAL COMMUNICATION CONTAINS A CASH OFFER TO SETTLE THE CLAIMS FILED IN THE PG&amp;E BANKRUPTCY</p>
        <p class='oneLine'>You are receiving this e-mail pursuant to the <a href="{{OfferProceduresURL}}" target="_blank">Offer Procedures</a> approved by the Bankruptcy Court. &nbsp;The letter attached to this email contains a cash offer by PG&amp;E to settle and fully resolve the securities claims you filed in the PG&amp;E bankruptcy and any and all claims arising out of your trading in PG&amp;E securities, subject to the terms of the Settlement Agreement.</p>
        <p class='oneLine'>To review and accept the Settlement Offer, please visit the <a href="{{SettlementPortalURL}}" target="_blank">Settlement Portal</a> using the unique username and password provided below:</p>
        <p><strong>Username:</strong>&nbsp; &nbsp; &nbsp; &nbsp; &nbsp;{{UserName}}</p>
        <p><strong>Password:</strong>&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Redacted</p>
        <p class='oneLine'>If the Settlement Agreement is acceptable to you, you will be able to electronically sign it on the Settlement Portal without incurring any additional costs. &nbsp;If you electronically sign the Settlement Agreement, PG&amp;E will send you a check in the amount of the Settlement Offer within 14 days of your electronic signature.</p>
        <p><br></p>
        <p class='oneLine'>Additional information can be found in the attached letter, as well as the <a href="{{OfferProceduresURL}}" target="_blank">Offer Procedures</a>. PG&amp;E encourages you to read both carefully.</p>
        <p>&nbsp;</p>
    </body>
</html>
`,
};

export const settlements = {
  get: (id: number) => get<SingleSettlementResponse>(`v1/Settlements/${id}`),
  put: (id: number, data: SettlementDataPut, regenerate?: boolean) => {
    const regenParam = regenerate ? '?regenerateAgeement=true' : '';
    return put<{}>(`v1/Settlements/${id}${regenParam}`, data);
  },

  agreementGenerate: (id: number) =>
    post<SettlementGenerateAgreementResponse>(
      `v1/Settlements/${id}/agreement/generate`,
    ),
  agreementDownload: (id: number) =>
    downloadFileGet(`v1/Settlements/${id}/agreement/download`),

  statuses: () => get<SettlementStatusResponse[]>('v1/Settlements/statuses'),

  documentTypes: () => get<SettlementDocumentType[]>('v1/Settlements/documenttypes'),

  getDocuments: (id: number) =>
    get<SettlementDocumentGet[]>(`v1/Settlements/${id}/documents`),
  postDocuments: (id: number, params: SettlementDocumentPost[]) =>
    post<SettlementDocumentPost>(`v1/Settlements/${id}/documents`, params),
  downloadDocument: (id: number, documentId: number, fileName: string) =>
    downloadFile(`v1/Settlements/${id}/documents/${documentId}/download`, fileName),

  // emailPreview: () => get<SettlementEmailPreview>('v1/Settlements/emailPreview'),
  emailPreview: () => promiseTimer(1000, EMAIL_PREVIEW_TEXT),
};
